import React, { createContext, useContext, useState } from 'react'
import { db, invokeFunction } from '../services/supabase'
import {
  mapExercise,
  mapMember,
  mapResult,
  mapSettings,
} from '../services/mappers'

const DataContext = createContext()

export const useData = () => {
  const context = useContext(DataContext)
  if (!context) {
    throw new Error('useData must be used within a DataProvider')
  }
  return context
}

export const DataProvider = ({ children }) => {
  const [members, setMembers] = useState([])
  const [exercises, setExercises] = useState([])
  const [kioskExercises, setKioskExercises] = useState([])
  const [results, setResults] = useState([])
  const [settings, setSettings] = useState(null)
  const [loading, setLoading] = useState(false)

  const fetchMembers = async () => {
    try {
      const { data, error } = await db()
        .from('members')
        .select('id, name, role, is_active, created_at, last_login')
        .eq('is_active', true)
        .order('name')
      if (error) throw error
      setMembers((data || []).map(mapMember))
    } catch (error) {
      console.error('Failed to fetch members:', error)
    }
  }

  const fetchExercises = async () => {
    try {
      const { data, error } = await db()
        .from('exercises')
        .select('*')
        .eq('is_active', true)
        .order('name')
      if (error) throw error
      setExercises((data || []).map(mapExercise))
    } catch (error) {
      console.error('Failed to fetch exercises:', error)
    }
  }

  const fetchKioskExercises = async () => {
    try {
      const { data, error } = await db()
        .from('exercises')
        .select('*')
        .eq('is_active', true)
        .eq('show_in_kiosk', true)
        .order('name')
      if (error) throw error
      setKioskExercises((data || []).map(mapExercise))
    } catch (error) {
      console.error('Failed to fetch kiosk exercises:', error)
    }
  }

  const fetchResults = async (filters = {}) => {
    try {
      setLoading(true)
      let query = db()
        .from('results')
        .select(`
          *,
          members ( id, name ),
          exercises ( id, name, mode, unit, cardio_metric, subs )
        `)
        .order('created_at', { ascending: false })

      if (filters.memberId) query = query.eq('member_id', filters.memberId)
      if (filters.exerciseId) query = query.eq('exercise_id', filters.exerciseId)
      if (filters.subId) query = query.eq('sub_id', filters.subId)
      if (filters.from) query = query.gte('created_at', filters.from)
      if (filters.to) query = query.lte('created_at', filters.to + 'T23:59:59')
      if (filters.limit) query = query.limit(parseInt(filters.limit, 10))
      else query = query.limit(200)

      const { data, error } = await query
      if (error) throw error
      setResults((data || []).map(mapResult))
    } catch (error) {
      console.error('Failed to fetch results:', error)
    } finally {
      setLoading(false)
    }
  }

  const fetchSettings = async () => {
    try {
      const { data, error } = await db().from('settings').select('*').limit(1).maybeSingle()
      if (error) throw error
      setSettings(data ? mapSettings(data) : null)
    } catch (error) {
      console.error('Failed to fetch settings:', error)
    }
  }

  const searchMembers = async (query) => {
    try {
      if (!query || !String(query).trim()) return []
      const { data, error } = await db()
        .from('members')
        .select('id, name')
        .eq('is_active', true)
        .ilike('name', `%${query.trim()}%`)
        .order('name')
        .limit(12)
      if (error) throw error
      return (data || []).map(mapMember)
    } catch (error) {
      console.error('Failed to search members:', error)
      return []
    }
  }

  const fetchMembersWithPasswords = async () => {
    try {
      return await invokeFunction('members', { action: 'with-passwords' })
    } catch (error) {
      console.error('Failed to fetch members with passwords:', error)
      return []
    }
  }

  const fetchMemberDetails = async (memberId) => {
    try {
      const data = await invokeFunction('members', { action: 'details', id: memberId })
      return { success: true, data }
    } catch (error) {
      return {
        success: false,
        error: error.data?.error || error.message || 'Failed to fetch member details',
      }
    }
  }

  const createMember = async (memberData) => {
    try {
      const data = await invokeFunction('members', {
        action: 'create',
        name: memberData.name,
        password: memberData.password,
      })
      await fetchMembers()
      return { success: true, data }
    } catch (error) {
      return {
        success: false,
        error: error.data?.error || error.message || 'Failed to create member',
      }
    }
  }

  const deleteMember = async (memberId) => {
    try {
      await invokeFunction('members', { action: 'delete', id: memberId })
      await fetchMembers()
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.data?.error || error.message || 'Failed to delete member',
      }
    }
  }

  const createExercise = async (exerciseData) => {
    try {
      const data = await invokeFunction('exercises', {
        action: 'create',
        ...exerciseData,
      })
      await fetchExercises()
      await fetchKioskExercises()
      return { success: true, data: mapExercise(data) }
    } catch (error) {
      return {
        success: false,
        error: error.data?.error || error.message || 'Failed to create exercise',
      }
    }
  }

  const updateExercise = async (exerciseId, exerciseData) => {
    try {
      const data = await invokeFunction('exercises', {
        action: 'update',
        id: exerciseId,
        ...exerciseData,
      })
      await fetchExercises()
      await fetchKioskExercises()
      return { success: true, data: mapExercise(data) }
    } catch (error) {
      return {
        success: false,
        error: error.data?.error || error.message || 'Failed to update exercise',
      }
    }
  }

  const deleteExercise = async (exerciseId) => {
    try {
      await invokeFunction('exercises', { action: 'delete', id: exerciseId })
      await fetchExercises()
      await fetchKioskExercises()
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.data?.error || error.message || 'Failed to delete exercise',
      }
    }
  }

  const createResult = async (resultData) => {
    try {
      const { data, error } = await db()
        .from('results')
        .insert({
          member_id: resultData.memberId,
          exercise_id: resultData.exerciseId,
          sub_id: resultData.subId,
          payload: resultData.payload,
        })
        .select(`
          *,
          members ( id, name ),
          exercises ( id, name, mode, unit, cardio_metric, subs )
        `)
        .single()
      if (error) throw error
      return { success: true, data: mapResult(data) }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to create result',
      }
    }
  }

  const deleteResult = async (resultId) => {
    try {
      const { error } = await db().from('results').delete().eq('id', resultId)
      if (error) throw error
      return { success: true }
    } catch (error) {
      return {
        success: false,
        error: error.message || 'Failed to delete result',
      }
    }
  }

  const updateSettings = async (settingsData) => {
    try {
      const data = await invokeFunction('exercises', {
        action: 'update-settings',
        ...settingsData,
      })
      const mapped = mapSettings(data)
      setSettings(mapped)
      return { success: true, data: mapped }
    } catch (error) {
      return {
        success: false,
        error: error.data?.error || error.message || 'Failed to update settings',
      }
    }
  }

  const value = {
    members,
    exercises,
    kioskExercises,
    results,
    settings,
    loading,
    fetchMembers,
    fetchExercises,
    fetchKioskExercises,
    fetchResults,
    fetchSettings,
    searchMembers,
    fetchMembersWithPasswords,
    fetchMemberDetails,
    createMember,
    deleteMember,
    createExercise,
    updateExercise,
    deleteExercise,
    createResult,
    deleteResult,
    updateSettings,
  }

  return (
    <DataContext.Provider value={value}>
      {children}
    </DataContext.Provider>
  )
}
